<template>
  <div v-bind="$attrs" :class="fullWidth ? 'd-block' : 'd-inline-block'">
    <v-btn
      :tag="getButtonTag"
      :aria-label="ariaLabel"
      :href="href"
      :to="to"
      :target="target"
      :disabled="disabled ?? loading"
      :loading="loading && !title"
      :prepend-icon="prependIcon"
      :icon="!title && icon ? icon : undefined"
      :size="icon && !title ? 36 : undefined"
      :type="submit ? 'submit' : 'button'"
      :download="download"
      :class="{
        'so-button--large': large,
        'w-100': fullWidth,
      }"
      :ripple="ripple"
      v-bind="getButtonProps"
      class="so-button text-capitalize rounded"
      style="letter-spacing: normal"
      :data-testid="dataTestId"
      :data-dd-action-name="dataDdActionName ?? title"
      @click="$emit('click', $event)"
    >
      <template v-if="title" #default>
        <v-progress-circular
          v-if="loading"
          :size="16"
          :width="2"
          indeterminate
          class="mr-2"
        />
        {{ title }}
        <v-icon
          v-if="type === 'link'"
          icon="chevron-right"
          :disabled="disabled"
          end
        />
        <v-icon v-else-if="icon" :icon="icon" :disabled="disabled" end />
      </template>
    </v-btn>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue"

  type SoButtonTypeProps = {
    color?: string
    size?: string
    flat?: boolean
    class?: string
    variant?: "flat" | "text" | "elevated" | "tonal" | "outlined" | "plain"
    ripple?: boolean
    density?: "compact" | "comfortable" | "default"
  }

  interface SoButtonTypes {
    readonly [char: string]: SoButtonTypeProps
  }

  const BUTTON_TYPES: SoButtonTypes = {
    primary: {
      color: "secondary",
      class: "so-button--primary",
      flat: true,
    },
    inverted: {
      color: "inverted",
      class: "so-button--inverted",
      flat: true,
    },
    secondary: {
      class: "so-button--secondary bg-surface font-weight-light",
      variant: "outlined",
    },
    success: {
      color: "success",
      class: "so-button--success",
      flat: true,
    },
    danger: {
      color: "danger",
      class: "so-button--danger",
      flat: true,
    },
    icon: {
      variant: "plain",
      class: "so-button--icon",
    },
    info: {
      color: "info",
      class: "so-button--info",
      flat: true,
    },
    "table-action": {
      size: "small",
      variant: "outlined",
      class: "so-button--table-action bg-surface font-weight-light",
    },
    "navbar-link": {
      variant: "plain",
      class: "so-button--navbar-link",
    },
    "navbar-icon": {
      flat: true,
      variant: "plain",
      class: "ml-2 so-button--navbar-link",
    },
    "sidebar-icon": {
      ripple: false,
      size: "x-small",
      variant: "plain",
      class: "so-button--sidebar-icon",
      density: "compact",
    },
    text: {
      flat: true,
      variant: "text",
      class: "text-primary so-button--text font-weight-light",
    },
    link: {
      variant: "plain",
      class: "text-primary so-button--link font-weight-light px-0",
    },
  }

  export default defineComponent({
    inheritAttrs: false,
    props: {
      title: {
        type: String,
        default: null,
      },
      icon: {
        type: String,
        default: null,
      },
      ariaLabel: {
        type: String,
        default: undefined,
      },
      large: {
        type: Boolean,
        default: false,
      },
      prependIcon: {
        type: String,
        default: undefined,
      },
      type: {
        type: String,
        default: "secondary",
        validator: (val: string) => val in BUTTON_TYPES,
      },
      href: {
        type: String,
        default: null,
      },
      to: {
        type: [String, Object],
        default: undefined,
      },
      target: {
        type: String,
        default: null,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      submit: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: undefined,
      },
      ripple: {
        type: Boolean,
        default: true,
      },
      fullWidth: {
        type: Boolean,
        default: false,
      },
      download: {
        type: Boolean || null,
        default: null,
      },
      dataTestId: {
        type: String,
        default: "so-button",
      },
      dataDdActionName: {
        type: String,
        default: null,
      },
    },
    emits: ["click"],

    computed: {
      getButtonProps() {
        return BUTTON_TYPES[this.type]
      },
      getButtonTag() {
        if (this.to) return "router-link"
        if (this.href) return "a"
        return "button"
      },
    },
  })
</script>

<style scoped lang="css">
  .so-button--primary:focus,
  .so-button--secondary:focus,
  .so-button--success:focus,
  .so-button--danger:focus {
    outline: var(--theme-outline);
  }
  .so-button--inverted:focus {
    outline: var(--theme-outline);
    outline-color: rgb(var(--v-theme-sky-light));
  }
  .so-button--navbar-link.v-btn--active {
    opacity: 1;
  }
  .so-button--sidebar-icon {
    height: 24px !important;
    min-width: 24px !important;
    font-size: 1rem;
    padding: 0 !important;
    align-content: center;
  }
  .so-button--large {
    height: 48px !important;
    min-width: 48px !important;
    font-size: 1rem;
    padding: 12px 16px;
  }

  :deep(.so-button.v-btn--disabled .v-btn__overlay) {
    background-color: rgb(var(--v-theme-background));
    z-index: 1;
  }

  .so-button--primary {
    background-color: rgb(var(--v-theme-success)) !important;
    color: rgb(var(--v-theme-on-success)) !important;
  }

  .so-button--primary.v-btn--disabled {
    background-color: rgb(var(--v-theme-disabled)) !important;
  }

  .so-button--link {
    opacity: 1;
  }

  .so-button.v-btn--size-default {
    font-size: var(--font-size-default);
  }
</style>
